import { Link } from 'react-router-dom';
import './Home.css';

// Brain Health marketing landing page.
//
// Audience priority (most to least frequent):
//   1. Activity directors arriving from a Quiltt sales email — already
//      partially sold, want validation + FAQs
//   2. Executive directors / regional ops doing diligence — need
//      positioning, evidence, pricing
//   3. Cold visitors — need the "what is this and why should I care"
//   4. Individual buyers — added in Phase 2B. Less frequent than
//      community traffic but a real audience now that the consumer
//      $4.99/mo SKU is live.
//
// The page reads top-to-bottom with progressive depth: hook →
// differentiators → evidence + Dr. Rob → engagement as care →
// how setup works → pricing → FAQ → CTA. Sign-in links live in a
// quiet footer block — they're not the primary action on a marketing
// page but need to stay reachable.
export default function Home() {
  return (
    <div className="home">
      {/* -------------------------------------------------------------
          HERO
          ------------------------------------------------------------- */}
      <section
        className="container home-section home-hero-section"
        style={{
          paddingTop: '7rem',
          paddingBottom: '12rem',
        }}
      >
        <span className="eyebrow fade-in-1">Play sharp.</span>

        <h1 className="home-hero fade-in-2">
          Brain health, in service of <em>independence</em>.
        </h1>

        <div className="home-lede fade-in-3">
          <p>
            Brain Health by Quiltt is an evidence-based program for senior
            living communities. Designed with Dr. Rob Winningham, it
            combines a brief cognitive assessment, daily personalized
            brain health games, and progress tracking your activity
            director will actually use.
          </p>
        </div>

        <div className="home-hero-cta fade-in-4">
          <a href="#interest" className="btn home-cta-primary">
            Bring it to your community
          </a>
          <a href="#differentiators" className="home-cta-secondary">
            What sets it apart ↓
          </a>
        </div>
      </section>

      {/* -------------------------------------------------------------
          DIFFERENTIATORS
          Replaces the earlier "three pillars" section. The pillars
          described how a resident uses the program (assessment →
          games → tracking); this section answers why a buyer should
          choose this over any other brain training product. The
          program-mechanics story is now carried by the expanded
          Evidence section and the For Activity Directors steps.
          ------------------------------------------------------------- */}
      <section id="differentiators" className="home-pillars-section">
        <div className="container">
          <span className="eyebrow">What sets it apart</span>
          <h2 className="home-section-title">
            A real program, not just a pile of games.
          </h2>

          <div className="home-pillars">
            <article className="home-pillar">
              <span className="home-pillar-number" aria-hidden="true">01</span>
              <h3 className="home-pillar-title">
                Brain health by design
              </h3>
              <p className="home-pillar-body">
                Designed in partnership with Dr. Rob Winningham. A brief
                cognitive assessment matches each resident to their level,
                and the games that follow are built to support inhibition,
                memory, attention, and processing speed: the cognitive
                functions that determine independence and level of care.
              </p>
            </article>

            <article className="home-pillar">
              <span className="home-pillar-number" aria-hidden="true">02</span>
              <h3 className="home-pillar-title">
                Signals that matter
              </h3>
              <p className="home-pillar-body">
                As residents play, time spent, frequency, and consistency
                become engagement signals. Those signals will surface on
                the Quiltt Connection Board alongside participation and
                mood, giving care teams the intelligence to respond.
              </p>
            </article>

            <article className="home-pillar">
              <span className="home-pillar-number" aria-hidden="true">03</span>
              <h3 className="home-pillar-title">
                A prescribable part of care
              </h3>
              <p className="home-pillar-body">
                Activity professionals can include specific brain health
                games in a resident's person-centered care plan, track
                outcomes, and act on what they see. Engagement is care.
                Brain Health makes that literal.
              </p>
            </article>
          </div>

          <p className="home-pricing-note">
            Connection Board surfacing from Brain Health is coming soon.
            Person-centered care planning is supported in Quiltt today;
            signals from Brain Health specifically are in development.
          </p>
        </div>
      </section>

      {/* -------------------------------------------------------------
          EVIDENCE / DR. ROB
          Expanded to carry the inhibition-and-independence story,
          which is the clinical case for why brain health games belong
          in senior living at all.
          ------------------------------------------------------------- */}
      <section className="home-evidence-section">
        <div className="container">
          <div className="home-evidence">
            <div className="home-evidence-portrait">
              <img
                src="/winningham-portrait.jpg"
                alt="Dr. Rob Winningham"
                className="home-evidence-portrait-img"
                width="240"
                height="240"
                loading="lazy"
              />
            </div>

            <div className="home-evidence-body">
              <span className="eyebrow">The Science Partner</span>
              <h2 className="home-evidence-title">
                Developed with a <em>leading researcher</em> in cognitive
                aging.
              </h2>
              <p className="home-evidence-text">
                Dr. Rob Winningham is Professor of Behavioral Sciences at
                Western Oregon University and author of <em>Train Your
                Brain</em>. He has spent his career studying which kinds of
                cognitive interventions actually work for older adults,
                and which don't.
              </p>
              <p className="home-evidence-text">
                The program reflects that research: brief, sound
                assessment; varied and targeted exercise; consistent
                measurement over time. We don't promise miracle cures.
                We do promise a serious program, grounded in the
                literature.
              </p>
              <p className="home-evidence-text">
                One concept from Dr. Winningham's work sits beneath
                everything Brain Health is built on: inhibition.
                Inhibition is what lets us stay focused, hold back
                impulses, and navigate daily life. It's one of the first
                cognitive functions to decline with age, and its loss is
                a primary factor in transitions from independent living
                to assisted living, and from assisted living to memory
                care.
              </p>
              <p className="home-evidence-text">
                Brain health games are designed to exercise inhibition
                alongside memory, attention, and processing speed. They
                are not a perk. They are a meaningful contribution to
                holistic health, cognitive reserve, and quality of life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------------
          ENGAGEMENT IS CARE
          New section. Speaks to executive directors and regional ops
          (audience #2). The Directors section below tells activity
          directors how to run the program; this one tells operators
          how the program fits into care delivery.
          ------------------------------------------------------------- */}
      <section className="home-pillars-section">
        <div className="container">
          <span className="eyebrow">Engagement Is Care</span>
          <h2 className="home-section-title">
            A <em>workflow</em>, not a philosophy.
          </h2>
          <p className="home-section-lede">
            At its simplest, the workflow looks like this. Activity
            professionals include specific brain health games in a
            resident's person-centered care plan. The resident plays.
            Their engagement signals (time spent, frequency, consistency)
            surface on the Quiltt Connection Board, alongside
            participation and mood. The care team sees patterns,
            identifies who's thriving and who needs a nudge, and
            responds.
          </p>
          <p className="home-section-lede">
            This is engagement as care in its most operational sense.
            Not a philosophy. A workflow. And it places activity
            professionals where they belong: as contributors to the
            care of the people they serve.
          </p>
          <p className="home-pricing-note">
            The person-centered care plan workflow is supported in
            Quiltt today. Connection Board signals from Brain Health
            specifically are coming soon.
          </p>
        </div>
      </section>

      {/* -------------------------------------------------------------
          FOR ACTIVITY DIRECTORS
          ------------------------------------------------------------- */}
      <section className="home-directors-section">
        <div className="container">
          <span className="eyebrow">For Activity Directors</span>
          <h2 className="home-section-title">
            Designed for the people who'll <em>run it</em>.
          </h2>
          <p className="home-section-lede">
            Setup takes minutes, not hours. There's nothing to install.
            It runs in any browser, on the devices you already have:
            tablets, phones, laptops, or large displays.
          </p>

          <ol className="home-steps">
            <li className="home-step">
              <span className="home-step-num">1</span>
              <div>
                <h3 className="home-step-title">We set up your community</h3>
                <p className="home-step-body">
                  You give us your community name and the email of the
                  administrator. We create the account and send a sign-in
                  link.
                </p>
              </div>
            </li>
            <li className="home-step">
              <span className="home-step-num">2</span>
              <div>
                <h3 className="home-step-title">You add your residents</h3>
                <p className="home-step-body">
                  One at a time, or as a list. Each resident gets a friendly
                  sign-in code like <code>MARGARET-742</code>. No passwords,
                  no email accounts to manage.
                </p>
              </div>
            </li>
            <li className="home-step">
              <span className="home-step-num">3</span>
              <div>
                <h3 className="home-step-title">Residents take the assessment and start playing</h3>
                <p className="home-step-body">
                  Self-administered for residents who are comfortable, or
                  staff-assisted for those who need support. After
                  assessment, daily recommended games are ready.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* -------------------------------------------------------------
          PRICING
          Two plans: Community (primary B2B offering) and Individual
          (consumer SKU launched Phase 2B). Community is listed first
          and is the editorially-emphasized plan since it's the main
          business. The Individual plan is presented as a parallel
          option, not a downsell — different audience, different fit.
          ------------------------------------------------------------- */}
      <section className="home-pricing-section">
        <div className="container">
          <span className="eyebrow">Pricing</span>
          <h2 className="home-section-title">
            Plans for your <em>community</em>, or just for <em>you</em>.
          </h2>

          <div className="home-pricing">
            <article className="home-pricing-card">
              <h3 className="home-pricing-tier">Community Subscription</h3>
              <div className="home-pricing-amount">
                <span className="home-pricing-currency">$</span>
                <span className="home-pricing-number">149</span>
                <span className="home-pricing-period"> / month</span>
              </div>
              <ul className="home-pricing-features">
                <li>Unlimited residents</li>
                <li>All assessments, brain health games, and tracking</li>
                <li>Activity director admin tools</li>
                <li>Cancel anytime</li>
              </ul>
              <a href="#interest" className="btn home-pricing-cta">
                Get started
              </a>
            </article>

            <article className="home-pricing-card">
              <h3 className="home-pricing-tier">Individual</h3>
              <div className="home-pricing-amount">
                <span className="home-pricing-currency">$</span>
                <span className="home-pricing-number">4.99</span>
                <span className="home-pricing-period"> / month</span>
              </div>
              <ul className="home-pricing-features">
                <li>Full library of brain health games</li>
                <li>Your own assessment and goals</li>
                <li>Personalized daily recommendations</li>
                <li>Cancel anytime</li>
              </ul>
              <Link to="/signup" className="btn home-pricing-cta">
                Start your subscription
              </Link>
            </article>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------------
          FAQ
          ------------------------------------------------------------- */}
      <section
        className="container home-section home-faq-section"
        style={{
          paddingTop: '9rem',
          paddingBottom: '11rem',
        }}
      >
        <span className="eyebrow">Frequently Asked</span>
        <h2 className="home-section-title">Questions, answered.</h2>

        <div className="home-faq">
          <details className="home-faq-item">
            <summary className="home-faq-q">
              How long does the assessment take?
            </summary>
            <div className="home-faq-a">
              About ten minutes. It's a brief, gentle screening with
              short recall tasks, simple drawing, and light reasoning.
              Most residents complete it without feeling tested.
            </div>
          </details>

          <details className="home-faq-item">
            <summary className="home-faq-q">
              What if a resident has cognitive impairment or dementia?
            </summary>
            <div className="home-faq-a">
              The assessment places each resident into one of four levels.
              Residents at lower levels see easier games and shorter
              sessions, with the option of staff-assisted play.
              Residents with significant impairment may benefit most when
              an activity director administers the assessment and joins
              their first few sessions.
            </div>
          </details>

          <details className="home-faq-item">
            <summary className="home-faq-q">
              What devices does it run on?
            </summary>
            <div className="home-faq-a">
              Any modern web browser. Most communities use tablets, but
              the program works on phones, laptops, and large displays
              equally well. There's nothing to download or install.
            </div>
          </details>

          <details className="home-faq-item">
            <summary className="home-faq-q">
              Do residents need their own logins?
            </summary>
            <div className="home-faq-a">
              Each resident gets a personal sign-in code like
              {' '}<code>MARGARET-742</code> that's easy to remember and
              say aloud. No passwords. No email accounts. The activity
              director generates and shares codes.
            </div>
          </details>

          <details className="home-faq-item">
            <summary className="home-faq-q">
              Can I subscribe as an individual, not through a community?
            </summary>
            <div className="home-faq-a">
              Yes. The individual plan is $4.99/month and gives you the
              same library of brain health games, personal assessment,
              and daily recommendations as a community resident, billed
              to you directly. You can{' '}
              <Link to="/signup" className="home-link">sign up here</Link>.
              Cancel anytime.
            </div>
          </details>

          <details className="home-faq-item">
            <summary className="home-faq-q">
              Is this medical or therapeutic?
            </summary>
            <div className="home-faq-a">
              No. Brain Health is a wellness program, not a clinical one.
              We don't diagnose, treat, or replace medical care. We do
              offer a structured, evidence-informed way to exercise
              cognitive function as part of everyday community life.
            </div>
          </details>

          <details className="home-faq-item">
            <summary className="home-faq-q">
              What happens if a resident moves out or stops participating?
            </summary>
            <div className="home-faq-a">
              You can archive a resident's account. Their data is
              preserved. If they return, you can restore them. If not,
              their seat opens for a new resident. The community
              subscription doesn't cap the number of residents.
            </div>
          </details>

          <details className="home-faq-item">
            <summary className="home-faq-q">
              Can family members participate or follow along?
            </summary>
            <div className="home-faq-a">
              Not in this version. Family member access (read-only
              progress views, with the resident's permission) is on
              our roadmap. Tell us if it would matter to your community
              and we'll prioritize it.
            </div>
          </details>

          <details className="home-faq-item">
            <summary className="home-faq-q">
              How is this different from Lumosity or other brain training apps?
            </summary>
            <div className="home-faq-a">
              Brain training apps are designed for personal productivity.
              Brain Health by Quiltt is designed for senior living. It's
              built with Dr. Rob Winningham around the cognitive aging
              research, fits the rhythms of community life, and is
              structured as part of care rather than a personal app. It
              has a staff-administered option, an activity director
              dashboard, and simple sign-in codes (no email accounts or
              app stores).
            </div>
          </details>
        </div>
      </section>

      {/* -------------------------------------------------------------
          CTA / INTEREST
          ------------------------------------------------------------- */}
      <section id="interest" className="home-cta-section">
        <div className="container">
          <span className="eyebrow">Get in Touch</span>
          <h2 className="home-cta-title">
            Interested in bringing Brain Health to your community?
          </h2>
          <p className="home-cta-lede">
            Send us a note. We'll set up a short call to walk through the
            program, answer your questions, and help you get your first
            residents started.
          </p>
          <a
            href="mailto:fpeyerl@quiltt.com?subject=Brain%20Health%20program%20-%20interested"
            className="btn home-cta-button"
          >
            Email us about Brain Health
          </a>
          <p className="home-cta-meta">
            Or write directly to{' '}
            <a href="mailto:fpeyerl@quiltt.com" className="home-link">
              fpeyerl@quiltt.com
            </a>.
          </p>
        </div>
      </section>

      {/* -------------------------------------------------------------
          QUIET SIGN-IN BLOCK
          For people who already have access and arrived here looking
          for the sign-in. Three audiences now: community residents
          (login code), individual subscribers (email magic link), and
          administrators (email magic link).
          ------------------------------------------------------------- */}
      <section className="container home-section home-signin-section">
        <hr className="editorial-rule" />
        <div className="home-signin">
          <div className="home-signin-block">
            <span className="eyebrow">For Residents</span>
            <p className="home-signin-text">
              If your community has given you a sign-in code, sign in
              here to start your daily set.
            </p>
            <Link to="/login" className="btn btn-secondary">
              Resident sign in
            </Link>
          </div>
          <div className="home-signin-block">
            <span className="eyebrow">For Individuals</span>
            <p className="home-signin-text">
              Subscribed individually? Sign in with your email and
              we'll send you a one-time link.
            </p>
            <Link to="/signup" className="btn btn-secondary">
              Sign in to your account
            </Link>
          </div>
          <div className="home-signin-block">
            <span className="eyebrow">For Administrators</span>
            <p className="home-signin-text">
              Activity directors and Quiltt staff sign in with their
              email. We'll send a one-time link.
            </p>
            <Link to="/admin/login" className="btn btn-secondary">
              Administrator sign in
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
